import { graphql } from 'gatsby';
import React from 'react';
import Layout from '../components/Layout';
import ImageSection, {
  ImageSectionProps,
  getSectionBgColor as getImageSectionBgColor,
} from '../components/sections/ImageSection';
import ProductsListSection, {
  ProductsListSectionProps,
  getSectionBgColor as getProductsListSectionBgColor,
} from '../components/sections/ProductsListSection';
import ResourceFileListSection, {
  ResourceFileListSectionProps,
  getSectionBgColor as getResourceFileListSectionBgColor,
} from '../components/sections/ResourceFileListSection';
import TextAndImageSection, {
  TextAndImageSectionProps,
  getSectionBgColor as getTextAndImageSectionBgColor,
} from '../components/sections/TextAndImageSection';
import TabbedListSection, {
  TabbedListSectionProps,
  getSectionBgColor as getTabbedListSectionBgColor,
} from '../components/sections/TabbedListSection';
import SEO from '../components/SEO';
import VideosSection, {
  VideosSectionProps,
  getSectionBgColor as getVideosSectionBgColor,
} from '../components/VideoSection';
import WeContactYou, {
  ContactFormSectionProps,
  getSectionBgColor as getContactFormSectionBgColor,
} from '../components/WeContactYou';
import Hero, { HeroProps } from '../components/Hero';
import NewsletterSection, {
  NewsletterProps,
  getSectionBgColor as getNewsletterSectionBgColor,
} from '../components/NewsletterSection';
import CtaSection, {
  CtaSectionProps,
  getSectionBgColor as getCtaSectionBgColor,
} from '../components/sections/CtaSection';
import { useWindowDimensions } from '../utils/hooks';

import * as styles from './Page.module.scss';

export const query = graphql`
  query GetPageById($id: String!) {
    sanityPage(id: { eq: $id }) {
      title
      slug {
        current
      }
      hero {
        withForm
        form {
          _rawTitle(resolveReferences: { maxDepth: 4 })
          fields {
            title
            _rawText(resolveReferences: { maxDepth: 4 })
            crmFieldId
            fieldType
            isFieldRequired
            withEmailValidation
            dropdownOptions {
              title
              crmValueStored
            }
          }
          submitButtonText
          fileToDownloadAfterSubmit {
            asset {
              url
            }
          }
          thankYouScreen {
            title
            _rawText(resolveReferences: { maxDepth: 4 })
          }
        }
        images {
          asset {
            url
            fluid(maxWidth: 1920) {
              ...GatsbySanityImageFluid
            }
          }
        }
        mobileImage {
          asset {
            url
            fluid(maxWidth: 1920) {
              ...GatsbySanityImageFluid
            }
          }
        }
        textOverTitle
        _rawIntroText(resolveReferences: { maxDepth: 4 })
        withProtectedText
      }
      sections {
        __typename
        ...TextAndImageSection
        ... on SanityTextAndImageSection {
          disabled
          sectionID
        }
        ...VideosSection
        ... on SanityVideosSection {
          disabled
          sectionID
        }
        ...ProductsListSection
        ... on SanityProductsListSection {
          disabled
          sectionID
        }
        ...ImageSection
        ... on SanityImageSection {
          disabled
          sectionID
        }
        ...ResourceFileListSection
        ... on SanityResourceFileListSection {
          disabled
          sectionID
        }
        ...TabbedListSection
        ... on SanityTabbedListSection {
          disabled
          sectionID
        }
        ...ContactFormSection
        ... on SanityContactFormSection {
          disabled
          sectionID
        }
        ...NewsletterSection
        ... on SanityNewsletterSection {
          disabled
          sectionID
        }
        ...CtaSection
        ... on SanityCtaSection {
          disabled
          sectionID
        }
      }
      seo {
        title
        description
        image {
          asset {
            url
          }
        }
      }
    }
  }
`;

export type SectionBgColor<ColorName extends string> = {
  name: ColorName;
  type: 'solid' | 'textured' | 'unknown';
};

export type SectionBgColorToType<ColorName extends string> = {
  [C in ColorName]: 'solid' | 'textured' | 'unknown';
};

export interface CommonSectionProps<ColorName extends string> {
  previousSectionBgColor: SectionBgColor<ColorName> | null;
  sectionID?: string;
}

export type Section =
  | (TextAndImageSectionProps & { __typename: 'SanityTextAndImageSection' } & {
      disabled: boolean | null;
    })
  | (VideosSectionProps & { __typename: 'SanityVideosSection' } & {
      disabled: boolean | null;
    })
  | (ProductsListSectionProps & { __typename: 'SanityProductsListSection' } & {
      disabled: boolean | null;
    })
  | (ImageSectionProps & { __typename: 'SanityImageSection' } & {
      disabled: boolean | null;
    })
  | (ResourceFileListSectionProps & { __typename: 'SanityResourceFileListSection' } & {
      disabled: boolean | null;
    })
  | (TabbedListSectionProps & { __typename: 'SanityTabbedListSection' } & {
      disabled: boolean | null;
    })
  | (ContactFormSectionProps & { __typename: 'SanityContactFormSection' } & {
      disabled: boolean | null;
    })
  | (NewsletterProps & { __typename: 'SanityNewsletterSection' } & {
      disabled: boolean | null;
    })
  | (CtaSectionProps & { __typename: 'SanityCtaSection' } & {
      disabled: boolean | null;
    });

type PageProps<ColorName extends string> = CommonSectionProps<ColorName> & {
  data: {
    sanityPage: {
      title: string;
      slug: {
        current: string;
      };
      hero: Omit<HeroProps, 'title'>;
      sections: Array<Section>;
      seo?: {
        title?: string;
        description?: string;
        image?: {
          asset: {
            url: string;
          };
        };
      };
    };
  };
};

export function getSectionBgColor(section: Section, windowWidth: number | null) {
  if (section.__typename === 'SanityContactFormSection') {
    return getContactFormSectionBgColor(section);
  } else if (section.__typename === 'SanityImageSection') {
    return getImageSectionBgColor(section, windowWidth);
  } else if (section.__typename === 'SanityCtaSection') {
    return getCtaSectionBgColor(section);
  } else if (section.__typename === 'SanityNewsletterSection') {
    return getNewsletterSectionBgColor(section);
  } else if (section.__typename === 'SanityProductsListSection') {
    return getProductsListSectionBgColor(section);
  } else if (section.__typename === 'SanityResourceFileListSection') {
    return getResourceFileListSectionBgColor(section);
  } else if (section.__typename === 'SanityTabbedListSection') {
    return getTabbedListSectionBgColor(section);
  } else if (section.__typename === 'SanityTextAndImageSection') {
    return getTextAndImageSectionBgColor(section);
  } else if (section.__typename === 'SanityVideosSection') {
    return getVideosSectionBgColor(section);
  } else {
    throw new Error(
      // @ts-ignore
      'Got unexpected previousSection with typename ' + section.__typename,
    );
  }
}

function Page<PreviousSectionColorName extends string>({
  data,
  previousSectionBgColor,
}: PageProps<PreviousSectionColorName>): React.ReactElement {
  const { sanityPage: page } = data;

  const enabledSections = page.sections.filter(section => !section.disabled);

  const windowDimensions = useWindowDimensions();

  return (
    <Layout className={styles.layout}>
      <SEO
        title={page.seo?.title || page.title}
        description={page.seo?.description}
        imageUrl={page.seo?.image?.asset.url}
      />
      <Hero title={page.title} {...page.hero} />

      {enabledSections.map((section, sectionIndex) => {
        const sectionKey = 'section-' + sectionIndex;

        const commonSectionProps: CommonSectionProps<any> = {
          previousSectionBgColor:
            sectionIndex === 0
              ? previousSectionBgColor
              : getSectionBgColor(enabledSections[sectionIndex - 1], windowDimensions.width),
        };

        if (section.__typename === 'SanityTextAndImageSection') {
          return (
            <TextAndImageSection
              key={sectionKey}
              {...section}
              {...commonSectionProps}
            ></TextAndImageSection>
          );
        }
        if (section.__typename === 'SanityVideosSection') {
          return (
            <VideosSection key={sectionKey} {...section} {...commonSectionProps}></VideosSection>
          );
        }
        if (section.__typename === 'SanityProductsListSection') {
          return (
            <ProductsListSection
              key={sectionKey}
              {...section}
              {...commonSectionProps}
            ></ProductsListSection>
          );
        }
        if (section.__typename === 'SanityImageSection') {
          return (
            <ImageSection key={sectionKey} {...section} {...commonSectionProps}></ImageSection>
          );
        }
        if (section.__typename === 'SanityResourceFileListSection') {
          return (
            <ResourceFileListSection
              key={sectionKey}
              {...section}
              {...commonSectionProps}
            ></ResourceFileListSection>
          );
        }
        if (section.__typename === 'SanityTabbedListSection') {
          return (
            <TabbedListSection
              key={sectionKey}
              {...section}
              {...commonSectionProps}
            ></TabbedListSection>
          );
        }
        if (section.__typename === 'SanityContactFormSection') {
          return (
            <WeContactYou
              key={sectionKey}
              {...section}
              {...commonSectionProps}
              isLandingPage
            ></WeContactYou>
          );
        }
        if (section.__typename === 'SanityNewsletterSection') {
          return (
            <NewsletterSection key={sectionKey} {...section} greenBackground></NewsletterSection>
          );
        }
        if (section.__typename === 'SanityCtaSection') {
          return <CtaSection key={sectionKey} {...section} {...commonSectionProps} />;
        }

        // @ts-ignore
        throw new Error('Got unexpected typename: ' + section.__typename);
      })}
    </Layout>
  );
}

export default Page;
